/**
 * Primitive Colour Tokens
 *
 * Raw colour values from the Figma "✅ Design System 2026" file.
 * These should NEVER be used directly in components.
 * Always use semantic tokens from semantic/colors-light.ts or colors-figma.ts instead.
 *
 * Last synced: 2026-10-07 from Figma file 3foqVdwaQfJxna3JPh4G0K.
 */

export const primitiveColors = {
  // Slate scale — Figma Colour Primitives → Slate
  slate100: "#F5F9FB",
  slate150: "#E1E8ED",
  slate200: "#E7EFF4",
  slate300: "#CEDBE4",
  slate400: "#ADC2D1",
  slate500: "#7992A6",
  slate600: "#5C778A",
  slate700: "#3A5E78",
  slate800: "#25445B",
  slate900: "#092D46",

  // Neutral scale — Figma Colour Primitives → Neutral
  neutral25: "#F8FAFC",
  neutral50: "#F5F9FB",
  neutral100: "#E6EDF3",
  neutral200: "#CFD4DA",
  neutral300: "#C9D1D9",
  neutral400: "#8B949E",
  neutral450: "#6E7781",
  neutral500: "#57606A",
  neutral600: "#424A53",
  neutral700: "#30363D",
  neutral800: "#1C2128",
  neutral825: "#1B2631",
  neutral900: "#0D1117",
  neutral925: "#09141C",
  neutral950: "#060818",
  neutral975: "#030F1A",

  // Blue scale — Figma Colour Primitives → Blue
  blue100: "#EEF0F7",
  blue200: "#D9DEF2",
  blue300: "#B9C4EE",
  blue400: "#8CA1EE",
  blue500: "#5D7DF4",
  blue600: "#1E4FDD",
  blue700: "#0C3CED",
  blue800: "#1334AE",
  blue900: "#0A1F4B",
  blue950: "#01114C",

  // Sky scale — Figma Colour Primitives → Sky
  sky400: "#58A6FF",
  sky700: "#0969DA",

  // Green scale — Figma Colour Primitives → Green
  green50: "#DFFBEE",
  green100: "#ECFBEA",
  green200: "#D7FAD1",
  green300: "#AFFAA3",
  green400: "#82FF6F",
  green500: "#31F613",
  green550: "#44B234",
  green600: "#22A40D",
  // Figma dark/brand/green — muted gauge/progress green
  green650: "#2E7A47", // toned down from #27C20F — was near-HDR on Android dark mode
  green700: "#27A414", // "Green-700 (N)" — numbers scale
  green750: "#1F7512",
  green800: "#268717",
  green900: "#236C18",
  green950: "#0D2D19",

  // Red scale — Figma Colour Primitives → Red
  red100: "#FFE7E0",
  red200: "#FFBDAD",
  red300: "#FF947A",
  red400: "#FF6C47",
  red500: "#FF4314",
  red600: "#E02D00",
  red700: "#AD2300", // "Red-700 (N)" — numbers scale
  red800: "#7A1800",
  red900: "#470E00",
  red950: "#4A1912",

  // Yellow scale — Figma Colour Primitives → Yellow
  yellow50: "#FFF8DB",
  yellow100: "#FFFBE0",
  yellow200: "#FFF5AB",
  yellow300: "#FFEF75",
  yellow400: "#FFE940",
  yellow500: "#FFE20A",
  yellow600: "#D4BB00",
  yellow700: "#9E8C00",
  yellow750: "#756800",
  yellow800: "#695C00",
  yellow900: "#332D00",
  yellow950: "#8B5D00",

  // Gold scale (premium) — Figma Colour Primitives → Gold
  gold200: "#F1E9D5",
  gold500: "#D1BA82",
  gold800: "#5C4A1A",

  // Extended palette — Figma Colour Primitives → Extended
  extendedPeach: "#F6C999",
  extendedCoral: "#F1A297",
  extendedRose: "#F7CED8",
  extendedLavender: "#F3D4FC",
  extendedViolet: "#DD97F2",
  extendedVioletDark: "#9E69D3",
  extendedIndigo: "#5966DB",
  extendedAzure: "#6398E7",
  extendedSalmon: "#F08778",
  extendedCyan: "#3A9AA8",
  extendedCyanLight: "#F0FBFC",
  extendedCyanDark: "#5CC8D4",
  extendedMagenta: "#A65D8A",
  extendedMagentaLight: "#FAF5F8",
  extendedMagentaDark: "#D48ABF",
  extendedTerracotta: "#A67B4B",
  extendedTerracottaLight: "#FFFBF7",
  extendedTerracottaDark: "#D4A670",
  extendedCyanBright: "#22D3EE",
  extendedJade: "#3FA46A",
  extendedCyan950: "#0E3A46",
  extendedJade950: "#17382F",
  extendedViolet950: "#332E47",
  extendedForest950: "#081C1F",
  extendedIris100: "#E7E4FF",
  extendedIris200: "#C6BEFE",
  extendedIris300: "#A799F3",
  extendedIris400: "#8976DC",
  extendedIris500: "#7059C3",

  // Base colours
  white: "#FFFFFF",
  black: "#000000",

  // Black overlays — Figma Neutral/Black (n%)
  overlayLight20: "rgba(0, 0, 0, 0.2)", // Neutral/Black (20%)
  overlayMedium40: "rgba(0, 0, 0, 0.4)", // Neutral/Black (40%)
  overlayDark: "rgba(0, 0, 0, 0.8)", // Neutral/Black (80%)
  overlayBlack: "rgba(0, 0, 0, 1)", // Neutral/Black (100%)
  overlayTransparent: "rgba(0, 0, 0, 0)", // Neutral/Black (0%)
  overlayScrim70: "#000000B2", // Neutral/Black-70

  // Slate-900 alpha overlays — Figma Slate/Slate-900 (n%) — used by light overlay/*
  slate900Alpha20: "rgba(9, 45, 70, 0.2)",
  slate900Alpha40: "rgba(9, 45, 70, 0.4)",
  slate900Alpha80: "rgba(9, 45, 70, 0.8)",

  // White overlays — Figma White/White-n
  whiteOverlay4: "rgba(255, 255, 255, 0.04)", // White/White-4
  whiteOverlay6: "rgba(255, 255, 255, 0.06)", // White/White-6
  whiteOverlay8: "rgba(255, 255, 255, 0.08)", // White/White-8
  whiteOverlay10: "rgba(255, 255, 255, 0.10)", // White/White-10
  whiteOverlay12: "rgba(255, 255, 255, 0.12)", // White/White-12
  whiteOverlay20: "rgba(255, 255, 255, 0.20)", // (local — no Figma equivalent; used by dark action/selected literal)
  whiteOverlay30: "rgba(255, 255, 255, 0.31)", // White/White-31
  whiteOverlay50: "rgba(255, 255, 255, 0.50)", // White/White-50

  // Glass surfaces — Figma Neutral/Glass*
  neutralGlass: "rgba(21, 25, 28, 0.5)", // Neutral/Glass
  neutralGlass10: "rgba(21, 25, 28, 0.10)", // Neutral/Glass-10
  neutralGlass80: "rgba(21, 25, 28, 0.80)", // Neutral/Glass-80

  // Neutral alpha colours used by subtle decorative dividers
  neutral300Alpha30: "rgba(201, 209, 217, 0.3)",
  gray500Alpha30: "rgba(128, 128, 128, 0.3)",

  // Glass gradients (dark UI icon containers) — Figma Glass/*
  glassGradientStart: "rgba(52, 54, 60, 0.7)", // Glass/Gradient-start
  glassGradientEnd: "rgba(28, 30, 32, 0.4)", // Glass/Gradient-end
  glassIconStart: "rgba(105, 106, 110, 0.3)", // Glass/Icon-start
  glassIconEnd: "rgba(28, 30, 32, 0.3)", // Glass/Icon-end

  // Gold pending accents — Figma Gold/Gold-pending*
  goldPending: "rgba(184, 162, 0, 0.8)", // Gold/Gold-pending
  goldPendingBg: "rgba(184, 162, 0, 0.12)", // Gold/Gold-pending-bg
  goldPendingBorder: "rgba(184, 162, 0, 0.25)", // Gold/Gold-pending-border
  goldPendingSubtle: "rgba(184, 162, 0, 0.15)", // Gold/Gold-pending-subtle

  // Progress ring subtle track — Figma Green/Green-track-subtle
  greenTrackSubtle: "rgba(216, 247, 212, 0.1)",

  // --- App 2026 redesign (V3) additions — Figma file 7kqf9xLAbMhr6ZHPXFLlKg ---

  // Blue-600 tints — V3 chips, count pills, icon-tile washes, band fills
  blue600Alpha7: "rgba(30, 79, 221, 0.07)",
  blue600Alpha10: "rgba(30, 79, 221, 0.10)",
  blue600Alpha12: "rgba(30, 79, 221, 0.12)",
  blue600Alpha14: "rgba(30, 79, 221, 0.14)",
  blue600Alpha20: "rgba(30, 79, 221, 0.2)",
  blue600Alpha40: "rgba(30, 79, 221, 0.4)",

  // Green-900 tint — V3 success badge/pill fill (Figma success-bg at 14%)
  green900Alpha14: "rgba(35, 108, 24, 0.14)",

  // Green-900 tint — info-callout success fill (Figma dark/bg/success/20)
  green900Alpha20: "rgba(35, 108, 24, 0.2)",

  // Green-900 soft stroke — V3 success tile borders (mirrors blue600Alpha40 weight)
  green900Alpha40: "rgba(35, 108, 24, 0.4)",

  // Red-700 tint — V3 negative movement pill fill (mirrors success tint weight)
  red700Alpha14: "rgba(173, 35, 0, 0.14)",

  // Red-800 tint — info-callout error fill (Figma dark/bg/error/20)
  red800Alpha20: "rgba(122, 24, 0, 0.2)",

  // Tint ladders - Figma Primitive Colours <hue>-<alpha%>
  red400Alpha14: "#FF6C4724",
  red400Alpha20: "#FF6C4733",
  red400Alpha40: "#FF6C4766",
  red800Alpha12: "#7A18001F",
  red800Alpha14: "#7A180024",
  red800Alpha40: "#7A180066",
  green900Alpha12: "#236C181F",
  extendedForestAlpha12: "#2E7A471F",
  extendedForestAlpha40: "#2E7A4766",
  neutral400Alpha20: "#8B949E33",

  // Amber — V3 pending strip (Figma dark/pending #FFCC00)
  amber500: "#FFCC00",
  amber500Alpha8: "rgba(255, 204, 0, 0.08)",
  amber500Alpha20: "rgba(255, 204, 0, 0.2)",
  amber500Alpha65: "rgba(255, 204, 0, 0.65)",
  amber500Alpha90: "#FFCC00E5",

  // Neutral-400 at 14% — Figma dark/border/strong-plus (glass icon-button stroke, hairlines)
  neutral400Alpha12: "rgba(139, 148, 158, 0.12)",
  neutral400Alpha14: "rgba(139, 148, 158, 0.14)",

  // Neutral-400 at 8% — Figma dark/border/divider (rules inside a card)
  neutral400Alpha08: "rgba(139, 148, 158, 0.08)",

  // 12% tone tints — V3 timeline icon tiles (Figma dark/text/success, feedback/notification, text/secondary at 12%)
  green500Alpha12: "rgba(49, 246, 19, 0.12)",
  amber500Alpha12: "rgba(255, 204, 0, 0.12)",
  neutral300Alpha12: "rgba(201, 209, 217, 0.12)",

  // Green-400 at 12% — Figma dark/product/entity/if-isa-bg
  green400Alpha12: "rgba(130, 255, 111, 0.12)",
} as const;

export type PrimitiveColor = keyof typeof primitiveColors;
export type PrimitiveColorValue = (typeof primitiveColors)[PrimitiveColor];
